import restClient from "api/restClient";
import { Button } from "components/Button/Button";
import { Text } from "components/Form/components/Text/Text";
import { requiredValidator, yamlValidator } from "components/Form/Form.utils";
import { IconCollection } from "components/Icon/IconCollection";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import { BlockingNavigationModal } from "components/Prompt/BlockingNavigationModal";
import { NAVIGATION_PRIORITIES } from "components/Prompt/NavigationPromptContext";
import { SpinnerAnimation } from "components/SpinnerAnimation/SpinnerAnimation";
import RecordTable from "components/Table/RecordTable";
import { TableColumn } from "components/Table/tableUtils";
import { DocsInfoTooltip } from "components/Tooltip/DocsInfoTooltip";
import { useMatchingFields } from "containers/Filter/FilterBuilder/useMatchingFields";
import { FeConfigType } from "config/config";
import { actions as resourcesActions } from "containers/Resources/Resources.reducer";
import { useMigrateOnSaveParams } from "containers/Resources/screens/ResourcesSingle/useCanCommitOnSave";
import { Record_Metadata } from "graphql/generated/graphql";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { actions as dataStoreAction } from "utils/DataStore/DataStore.reducer";
import strings from "utils/strings";
import { getIri, getSearchObj } from "utils/utils";
import {
    baselineEditValue,
    deriveMetadataRows,
    effectiveEditValue,
    hasAnyUnsavedChange,
    MetadataEditValue,
    MetadataRow,
    showRowActions,
} from "./deriveMetadataRows";
import styles from "./MetadataList.module.scss";

type EditsMap = Record<string, MetadataEditValue>;

interface MetadataListProps {
    id: string;
    isDisabled: boolean;
    setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
    resourceName?: FeConfigType;
}

/**
 * Record-metadata editor, rendered as a RecordTable with THREE editable columns
 * (Key | Value | Actions). Key and value are edited directly in their cells — no
 * expand/unfold (Margot, operator-task-fe-convert-independent-renderers-to-recordtable).
 * Because a single react-final-form cannot span mui-datatables' independently
 * rendered cells, the per-row working edits are lifted into this component as a
 * map keyed by stable row id; the cells render controlled inputs bound to it.
 * Behaviour preserved from the previous per-row form stack: required key, YAML
 * validation, create/update/delete GraphQL mutations with commit:false + migrate
 * params, dirty -> parent unsaved-changes tracking, and the navigate-away guard
 * (now a single guard for the whole list).
 */
export const MetadataList = ({
    id,
    isDisabled,
    setHasUnsavedChanges,
    resourceName,
}: MetadataListProps) => {
    const dispatch = useDispatch();
    const history = useHistory();

    const key = `recordMetadataList-${id}`;

    const [isLoading, setLoading] = useState(false);
    const [isNewForm, setNewForm] = useState(false);
    // The id of the row whose save/delete is currently in flight, so the busy
    // indicator shows ON that row's Confirm/trash button (the standard button
    // loader) rather than as a floating overlay that the table hides (Margot,
    // operator-task-fe-convert-independent-renderers-to-recordtable).
    const [busyRowId, setBusyRowId] = useState<string | null>(null);

    // Per-row working edits keyed by stable row id (the metadata id, or the
    // synthetic new-row id). Absent => the row shows its persisted baseline.
    const [edits, setEdits] = useState<EditsMap>({});
    // Mirror the latest edits so the stable action callbacks can read the live
    // value without being recreated (and remounting the inputs) on every keystroke.
    const editsRef = useRef(edits);
    editsRef.current = edits;

    const getRecordMetadataListSelector = (state) => state?.dataStore?.[key];
    const recordMetadataDataList: Record_Metadata[] = useSelector(
        getRecordMetadataListSelector
    )?.record_metadata_plain_list;

    const getData = useCallback(() => {
        setLoading(true);
        const filterStr = JSON.stringify(getSearchObj({ field: "record_id", value: id, op: "eq" }));
        new Promise((resolve, reject) => {
            dispatch({
                promise: { resolve, reject },
                type: dataStoreAction.START_FETCH_DATA,
                key,
                params: {
                    query: `query($filter: String, $order: String, $limit: Int, $offset: Int) {
                    record_metadata_plain_list(filter:$filter,order:$order,limit:$limit,offset:$offset){
                        data
                        id
                        key
                    },
                }`,
                    variables: { filter: filterStr, order: "+key", offset: 0 },
                },
            });
        })
            .then((data: any) => {
                const count = data?.record_metadata_plain_list?.length;
                dispatch({
                    type: resourcesActions.STORE_RESOURCE_DATA_WITH_PARTIAL_UPDATE,
                    iri: getIri(resourceName, id),
                    response: {
                        record_metadata_list_record_id: {
                            record_metadata_count: count,
                        },
                    },
                });
            })
            .catch((error) => error)
            .finally(() => setLoading(false));
    }, [dispatch, id, key, resourceName]);

    useEffect(() => {
        if (id) {
            getData();
        }
    }, [getData, id]);

    const { defaultSchemaValues } = useMatchingFields({
        activeTab: "record_metadata",
    });

    const rows: MetadataRow[] = useMemo(
        () => deriveMetadataRows(recordMetadataDataList, isNewForm ? defaultSchemaValues : null),
        [recordMetadataDataList, isNewForm, defaultSchemaValues]
    );

    useEffect(() => {
        setHasUnsavedChanges(hasAnyUnsavedChange(rows, edits));
    }, [rows, edits, setHasUnsavedChanges]);

    const migrateOnSaveParams = useMigrateOnSaveParams();
    const params = useMemo(
        () => ({ commit: false, ...migrateOnSaveParams }),
        [migrateOnSaveParams]
    );

    // --- edit-state mutators -------------------------------------------------

    /** Drop a row's working edit; also closes the pending new row when it is one. */
    const clearRow = useCallback((row: MetadataRow) => {
        setEdits((prev) => {
            if (!(row.id in prev)) return prev;
            const next = { ...prev };
            delete next[row.id];
            return next;
        });
        if (row.isNew) {
            setNewForm(false);
        }
    }, []);

    /** Update one field of a row's working edit (dropping it again if it reverts to baseline). */
    const setField = useCallback(
        (row: MetadataRow, field: keyof MetadataEditValue, value: string) => {
            setEdits((prev) => {
                const base = baselineEditValue(row);
                const current = prev[row.id] ?? base;
                const nextValue: MetadataEditValue = { ...current, [field]: value };
                const next = { ...prev };
                if (!row.isNew && nextValue.key === base.key && nextValue.data === base.data) {
                    delete next[row.id];
                } else {
                    next[row.id] = nextValue;
                }
                return next;
            });
        },
        []
    );

    const applyAction = useCallback(
        (actiontype: string, actionCallback: () => Promise<any>, row: MetadataRow) =>
            actionCallback()
                .then(() => {
                    getData();
                    clearRow(row);
                })
                .catch((error) =>
                    NotificationService.error(
                        <RestErrorNotification
                            titleOverride={`Sorry, couldn't ${actiontype} record metadata`}
                            error={error}
                        />
                    )
                )
                .finally(() => {
                    setLoading(false);
                    setBusyRowId(null);
                }),
        [getData, clearRow]
    );

    const confirmRow = useCallback(
        (row: MetadataRow) => {
            const eff = editsRef.current[row.id] ?? baselineEditValue(row);
            const action = row.isNew ? "create" : "update";
            setBusyRowId(row.id);
            const mutate = () =>
                restClient.post(
                    `/graphql`,
                    {
                        query: `mutation ${action}_record_metadata($record_id: String!, $key: String!, $data: SchemalessData, ${
                            action === "update" ? "$id: String!" : ""
                        }) {
                        ${action}_record_metadata(body: {record_id: $record_id, key: $key, data: $data}, ${
                            action === "update" ? "id: $id" : ""
                        }) {
                            id
                        }
                    }`,
                        variables: {
                            key: eff.key,
                            data: eff.data,
                            record_id: id,
                            id: row.metadata?.id,
                        },
                    },
                    { params }
                );
            applyAction(action, mutate, row);
        },
        [applyAction, id, params]
    );

    const deleteRow = useCallback(
        (row: MetadataRow) => {
            if (!row.metadata?.id) {
                // Pending new row — nothing persisted yet, just discard it.
                clearRow(row);
                return;
            }
            setBusyRowId(row.id);
            const mutate = () =>
                restClient.post(
                    "graphql",
                    {
                        query: `mutation delete_record_metadata($id: String!){
                        delete_record_metadata(id: $id)}`,
                        variables: { id: row.metadata.id },
                    },
                    { params }
                );
            applyAction("delete", mutate, row);
        },
        [applyAction, clearRow, params]
    );

    // --- columns (referentially stable so keystrokes don't rebuild the table) -
    // The cells read the live edit via editsRef.current / setField, so the
    // column definitions never need to change as the user types — which keeps
    // the focused input mounted and preserves the caret.

    const columns: TableColumn[] = useMemo(
        () => [
            {
                label: "Key",
                key: "key",
                shown: true,
                sortable: false,
                dto: (row: MetadataRow) => {
                    const eff = effectiveEditValue(row, editsRef.current);
                    const error = requiredValidator(eff.key, undefined);
                    const isLocked = isDisabled || busyRowId === row.id;
                    return (
                        <div className={styles.keyCell}>
                            <Text
                                name={`metadata-key-${row.id}`}
                                attributes={{}}
                                errorMessage={typeof error === "string" ? error : undefined}
                                inputProps={{
                                    value: eff.key,
                                    isReadOnly: isLocked,
                                    onChange: (e: any) => setField(row, "key", e.target.value),
                                }}
                            />
                        </div>
                    );
                },
            },
            {
                label: "Value",
                key: "value",
                shown: true,
                sortable: false,
                dto: (row: MetadataRow) => {
                    const eff = effectiveEditValue(row, editsRef.current);
                    const error = yamlValidator(eff.data);
                    const lineCount = (eff.data || "").split("\n").length;
                    const rowsAttr = Math.min(Math.max(lineCount, 1), 12);
                    const isLocked = isDisabled || busyRowId === row.id;
                    return (
                        <div className={styles.valueCell}>
                            <textarea
                                className={styles.valueTextarea}
                                aria-label={`metadata-value-${row.id}`}
                                data-testid={`metadata-value-${row.id}`}
                                value={eff.data}
                                rows={rowsAttr}
                                spellCheck={false}
                                disabled={isLocked}
                                aria-busy={busyRowId === row.id}
                                onChange={(e) => setField(row, "data", e.target.value)}
                            />
                            {error ? <span className={styles.valueError}>{error}</span> : null}
                        </div>
                    );
                },
            },
            {
                label: "Actions",
                key: "metaActions",
                shown: true,
                sortable: false,
                dto: (row: MetadataRow) => {
                    const edits_ = editsRef.current;
                    const eff = effectiveEditValue(row, edits_);
                    const invalid =
                        typeof requiredValidator(eff.key, undefined) === "string" ||
                        typeof yamlValidator(eff.data) === "string";
                    const isBusy = busyRowId === row.id;
                    return (
                        <div className={styles.actionsCellInner}>
                            {showRowActions(row, edits_) ? (
                                <>
                                    <Button
                                        label="Confirm"
                                        variation="primary"
                                        size="small"
                                        loading={isBusy}
                                        disabled={invalid || isDisabled || isBusy}
                                        onClick={() => confirmRow(row)}
                                    />
                                    <Button
                                        label="Cancel"
                                        variation="white"
                                        size="small"
                                        disabled={isBusy || isDisabled}
                                        onClick={() => clearRow(row)}
                                    />
                                </>
                            ) : null}
                            {!row.isNew ? (
                                <Button
                                    icon={IconCollection.deleteOutline}
                                    variation="icon"
                                    size="small"
                                    loading={isBusy && !showRowActions(row, edits_)}
                                    disabled={isBusy || isDisabled}
                                    onClick={() => deleteRow(row)}
                                />
                            ) : null}
                        </div>
                    );
                },
            },
        ],
        [isDisabled, setField, confirmRow, clearRow, deleteRow, busyRowId]
    );

    const tableOptions = useMemo(
        () => ({
            toolbar: "none" as const,
            toolbarFilters: false,
            selectableRows: "none" as const,
            showSearch: false,
            viewColumns: false,
            // A "normal" table: let the user drag column borders to re-balance
            // Key / Value / Actions widths (persisted per tableSettingsKey). The
            // default widths below (narrow Key, fixed Actions) are the starting
            // point; dragging overrides them (Margot).
            resizableColumns: true,
            showDensityToggle: false,
            pagination: false,
        }),
        []
    );

    return (
        <div style={{ position: "relative" }}>
            <BlockingNavigationModal
                navigate={history.push}
                shouldBlockNavigation={hasAnyUnsavedChange(rows, edits)}
                titleText={strings.navigateAwayUnsavedPrompt}
                priority={NAVIGATION_PRIORITIES.METADATA_FORM}
            />
            {/* Floating overlay only for the INITIAL fetch (before any rows have
                loaded). Per-row save/delete shows its busy state on that row's
                own button instead, so the indicator is never hidden behind the
                table. */}
            <SpinnerAnimation
                isLoading={isLoading && !recordMetadataDataList}
                className={styles.spinnerAnimation}
            />
            <div className={styles.metadataEditorTable}>
                <RecordTable
                    columns={columns}
                    data={rows}
                    totalItemCount={rows.length}
                    recordTypes="all"
                    isLoading={false}
                    tableSettingsKey="recordMetadataList"
                    options={tableOptions}
                />
            </div>
            <div className={styles.addMetadataContainer}>
                <Button
                    label="+ Add metadata record"
                    onClick={() => setNewForm(true)}
                    variation={"white"}
                    size={"small"}
                    disabled={isNewForm || isDisabled}
                />
                <DocsInfoTooltip
                    title="Record metadata"
                    urlPath={"/docs/accessing-and-manipulating-records#record-metadata"}
                />
            </div>
        </div>
    );
};
